// Card 13, labelled isobaths (R548, ui-map-interface.md §17.3.13).
//
// The current chart draws depth contours and BREAKS each line for its
// figure. Three contours ring every trench OUTSIDE its rim, at 14, 32 and 54
// world px out, and each one carries the sonar-unit figure of card 02 in
// the gap: 6, 4 and 2. The rings say where the trench is from the open
// water a fleet approaches it over, which is what the owner asked for on
// 2026-09-05 (R557): the first build stepped the contours INSIDE the rim.
//
// **THE LINE IS TRACED, NOT A SHADER TERM.** The terrain composite already
// draws iso-lines of its own channel, and it could draw three more for
// about three ALU. It cannot break one for a figure: a shader has no arc
// length and no idea where the gap should be. And the plane carries no
// distance outside a body at all. So the rings are traced on the CPU
// (`outerIsobathsOf`): the rim is read off the SAME plane the composite
// reads, a distance raster is laid round it, and the piece walks each loop
// to place the break.
//
// **THE GAP SITS AT THE LOOP'S LOWEST-Y POINT**, which is the top of the
// picture. That is where the card put it, and it is the one point of a loop
// that a reader finds without looking: the crown of the contour.
//
// **A RING DOES NOT CROSS ROCK OR ANOTHER TRENCH.** A run is cut where a
// point stands on a blocked cell, so a ring passes behind a ridge and stops
// at another trench's rim: a chart's isobath is drawn on the water.
//
// **A RING ENDS AT THE MAP EDGE (R2211).** The ring raster reaches past the
// plane, so every ring comes back closed, also round a trench on the edge
// (R2368). The piece cuts each loop into its pieces on the map, and each
// open piece takes its own gap and figure.
//
// **WITH THE BAND, THE FIGURE IS A SOUNDING (R2616).** While the hello
// carries the band field (`Hello.depth`, the flag `depth`), each figure
// writes the sounding in metres of the cell under its gap, the answer of
// `soundingMetres` that card 02 writes, so the soundings, the depth tints
// and the isobaths tell one depth story (v2-the-deep.md §13). A gap over
// a cell with no band keeps the figure of the pack.

import { CHART, TERRAIN_TRENCH_RIM } from '../../../gen/presentation';
import { outerIsobathsOf } from '../../../render/terrain-features';
import type { TerrainPlane } from '../../../render/terrain-features';
import { thinLoop } from '../../../render/offset-loop';
import { toRoman } from '../../../render/roman';
import { polylineLength, type Pt } from '../../../render/text-run';
import { COLS, ROWS, cellAt } from '../../../world';
import { soundingMetres } from './soundings';
import type { Loop } from '../../../render/contours';
import type { ChartContext, ChartReading, ChartSinks, Piece } from '../context';
import { asPts, crownOf, faceOf, lengthTo } from './shared';

/** The figures the piece reads out of its own table. */
const T = CHART.pieces.isobaths;

/**
 * Half the length of the straight path a figure is laid along, centred on
 * the gap and turned to the contour's tangent. It only has to be longer
 * than half the widest figure.
 */
const LABEL_HALF = 64;

/** One traced contour, broken for its figure. */
interface Contour {
  /** The loop, thinned, with the gap already taken out of it. */
  runs: Loop[];
  /** The two-point path the figure rides, through the crown of the gap. */
  label: Pt[];
  /** Which of the three offsets this is. It picks the dash and the alpha. */
  band: number;
}

/** The three dash patterns, flattened by the emitter into one key each. */
function dashOf(band: number): readonly number[] {
  const a = T.arr as Record<string, readonly number[]>;
  return a[`dash_${band}`] ?? [];
}

export class IsobathsPiece implements Piece {
  readonly id = 'isobaths' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The sea the fight happened in. It stays under a replay film. */
  readonly film = 'keep' as const;
  /**
   * The trace is zoom-free, so a band crossing is not a reason: the piece
   * keeps its geometry across the gate and repacks from it (R548). A
   * reading changes the figure alone, and the trace is reused for it.
   */
  readonly rebuildOn = ['hello', 'reading', 'survey'] as const;

  private contours: Contour[] = [];
  /** The plane the cached trace was read from, and the trace itself. */
  private tracedPlane: TerrainPlane | null = null;
  private traced: Loop[][][] = [];
  /** How many times the plane was traced. The test reads it. */
  traces = 0;
  /** The trace and the blocked mask the contours were cut from. */
  private cutTrace = -1;
  private cutBlocked: Uint8Array | null = null;
  /** How many times the traced loops were cut. The test reads it. */
  cuts = 0;
  /** The reading the labels were laid out for. */
  private laid: ChartReading = 'modern';

  /**
   * The plane arrives about two animation frames after the hello, so the
   * piece waits for it here. The controller builds a piece the first frame
   * its gate holds, which is the frame the plane lands.
   */
  visible(ctx: ChartContext): boolean {
    return ctx.terrain !== null && (ctx.scene.plane ?? null) !== null;
  }

  rebuild(ctx: ChartContext): void {
    this.laid = ctx.reading;
    const plane = ctx.scene.plane ?? null;
    const terrain = ctx.terrain;
    if (!plane || !terrain) { this.contours = []; this.cutTrace = -1; return; }
    const gap = T.fig.gap_px;
    if (this.tracedPlane !== plane) {
      // One trace per plane. It is the one expensive step of the piece, and
      // a reading change or a repack must not pay it again.
      this.traced = terrain.trenches.map((feature) => outerIsobathsOf(
        plane, feature, T.arr.offsets, COLS, ROWS, T.fig.rim_px, T.fig.field_texels,
      ));
      this.tracedPlane = plane;
      this.traces++;
    }
    const blocked = ctx.scene.blocked;
    // The cut reads the trace and the blocked mask alone, so a survey and a
    // reading keep it (R2897): the figures read the bands at the pack.
    if (this.cutTrace === this.traces && this.cutBlocked === blocked) return;
    this.cutTrace = this.traces;
    this.cutBlocked = blocked;
    this.cuts++;
    this.contours = [];
    for (const bands of this.traced) {
      bands.forEach((loops, band) => {
        for (const raw of loops) {
          const loop = thinLoop(raw, T.fig.step_px);
          // A ring that leaves the map is cut into its pieces on the map,
          // and each piece is cut on its own.
          const pieces = onMap(loop);
          const cuts = pieces === null
            ? [cutLoop(loop, gap)]
            : pieces.map((run) => cutOpen(run, gap));
          for (const cut of cuts) {
            if (!cut) continue;
            cut.band = band;
            if (blocked) cut.runs = cut.runs.flatMap((run) => onWater(run, blocked));
            this.contours.push(cut);
          }
        }
      });
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const ink = TERRAIN_TRENCH_RIM.color;
    const face = faceOf(ctx.reading);
    const depth = ctx.state.depthBands;
    const metres = depth ? soundingMetres(depth) : null;
    for (const c of this.contours) {
      const alpha = T.arr.alphas[c.band] ?? 0;
      if (alpha <= 0) continue;
      const dash = dashOf(c.band);
      const on = dash[0] ?? 0, off = dash[1] ?? 0;
      for (const run of c.runs) {
        for (let i = 1; i < run.length; i++) {
          sinks.segments.line(
            run[i - 1][0], run[i - 1][1], run[i][0], run[i][1],
            T.fig.width, ink, alpha, 0, on, off,
          );
        }
      }
      const mid = c.label.length === 2
        ? cellAt((c.label[0].x + c.label[1].x) / 2, (c.label[0].y + c.label[1].y) / 2, COLS, ROWS) : -1;
      const sounded = metres && mid >= 0 ? metres[mid] : 0;
      const figure = sounded > 0 ? String(sounded) : ctx.reading === 'historical'
        ? toRoman(T.arr.figures[c.band]).toLowerCase()
        : String(T.arr.figures[c.band]);
      // The figure in the gap, turned to the contour's own tangent. The
      // path already reads left to right, so `upright` is off.
      ctx.glyphs.wordAlong(
        sinks.quads, figure, c.label, face, T.text.main, ink,
        Math.min(1, alpha + T.fig.figure_alpha_lift),
        { anchor: 'centre', startS: LABEL_HALF, upright: false },
      );
    }
  }

  /** How many contours the last rebuild traced. The test reads it. */
  get count(): number { return this.contours.length; }

  /** The reading the labels stand in. The test reads it. */
  get reading(): ChartReading { return this.laid; }
}

/**
 * Breaks one closed loop for its figure and answers the one piece of line.
 *
 * The break is centred on the loop's lowest-y point. A loop shorter than
 * the gap itself is dropped: there would be no line left to draw, and a
 * figure floating on nothing states a depth for no contour.
 */
function cutLoop(loop: Loop, gap: number): Contour | null {
  if (loop.length < 3) return null;
  const total = polylineLength(asPts(loop), true);
  if (total <= gap * 2) return null;
  const crown = crownOf(loop);
  // The arc length of the crown, and the two ends of the break.
  const at = lengthTo(loop, crown);
  const half = gap / 2;
  const a = alongLoop(loop, at - half, total);
  const b = alongLoop(loop, at + half, total);
  // The run walks the loop from the far end of the break round to the
  // near end, so the whole contour is ONE polyline with the gap open.
  const run: Loop = [[b.x, b.y]];
  let s = 0;
  for (let i = 0; i < loop.length; i++) {
    const j = (crown + 1 + i) % loop.length;
    const prev = (j + loop.length - 1) % loop.length;
    s += Math.hypot(loop[j][0] - loop[prev][0], loop[j][1] - loop[prev][1]);
    if (s >= total - gap) break;
    run.push([loop[j][0], loop[j][1]]);
  }
  run.push([a.x, a.y]);
  return {
    runs: run.length >= 2 ? [run] : [],
    label: labelPath(a, b, loop[crown]),
    band: 0,
  };
}

/** The point `s` px along a closed loop. */
function alongLoop(loop: Loop, s: number, total: number): { x: number; y: number } {
  let at = ((s % total) + total) % total;
  for (let i = 0; i < loop.length; i++) {
    const p = loop[i], q = loop[(i + 1) % loop.length];
    const len = Math.hypot(q[0] - p[0], q[1] - p[1]);
    if (len === 0) continue;
    if (at > len) { at -= len; continue; }
    const t = at / len;
    return { x: p[0] + (q[0] - p[0]) * t, y: p[1] + (q[1] - p[1]) * t };
  }
  return { x: loop[0][0], y: loop[0][1] };
}

/**
 * Breaks one open run for its figure, as `cutLoop` breaks a closed loop.
 *
 * The break is centred on the run's lowest-y point that has half the gap of
 * line on both sides, so the gap and the figure stand on the run. A run no
 * longer than two gaps is dropped, for the reason `cutLoop` states.
 */
function cutOpen(run: Loop, gap: number): Contour | null {
  if (run.length < 2) return null;
  const s = [0];
  for (let i = 1; i < run.length; i++) {
    s.push(s[i - 1] + Math.hypot(run[i][0] - run[i - 1][0], run[i][1] - run[i - 1][1]));
  }
  const total = s[s.length - 1];
  if (total <= gap * 2) return null;
  const half = gap / 2;
  let crown = -1;
  for (let i = 0; i < run.length; i++) {
    if (s[i] < half || s[i] > total - half) continue;
    if (crown < 0 || run[i][1] < run[crown][1]) crown = i;
  }
  if (crown < 0) return null;
  const a = alongOpen(run, s, s[crown] - half);
  const b = alongOpen(run, s, s[crown] + half);
  const head: Loop = run.filter((_, i) => s[i] < s[crown] - half);
  head.push([a.x, a.y]);
  const tail: Loop = [[b.x, b.y]];
  for (let i = 0; i < run.length; i++) if (s[i] > s[crown] + half) tail.push(run[i]);
  return {
    runs: [head, tail].filter((r) => r.length >= 2),
    label: labelPath(a, b, run[crown]),
    band: 0,
  };
}

/**
 * The two-point path a figure rides: through the crown `c`, along the chord
 * of the gap from `a` to `b`, turned so the figure reads left to right.
 */
function labelPath(a: Pt, b: Pt, c: readonly [number, number]): Pt[] {
  const rot = upright(Math.atan2(b.y - a.y, b.x - a.x));
  const ux = Math.cos(rot), uy = Math.sin(rot);
  return [
    { x: c[0] - ux * LABEL_HALF, y: c[1] - uy * LABEL_HALF },
    { x: c[0] + ux * LABEL_HALF, y: c[1] + uy * LABEL_HALF },
  ];
}

/** The point at arc length `at` of an open run, whose arc lengths are `s`. */
function alongOpen(run: Loop, s: readonly number[], at: number): { x: number; y: number } {
  for (let i = 1; i < run.length; i++) {
    if (at > s[i]) continue;
    const len = s[i] - s[i - 1];
    const t = len === 0 ? 0 : (at - s[i - 1]) / len;
    const p = run[i - 1], q = run[i];
    return { x: p[0] + (q[0] - p[0]) * t, y: p[1] + (q[1] - p[1]) * t };
  }
  const last = run[run.length - 1];
  return { x: last[0], y: last[1] };
}

/**
 * The pieces of one traced loop that stand on the map, or null when the
 * whole loop does.
 *
 * The walk starts after a point off the map, so a piece that runs through
 * the loop's first point stays whole. Every traced ring is closed (R2368),
 * so the walk joins two pieces of line only where the loop itself runs.
 */
function onMap(loop: Loop): Loop[] | null {
  const off = loop.findIndex((p) => cellAt(p[0], p[1], COLS, ROWS) < 0);
  if (off < 0) return null;
  const out: Loop[] = [];
  let piece: Loop = [];
  for (let k = 1; k <= loop.length; k++) {
    const p = loop[(off + k) % loop.length];
    if (cellAt(p[0], p[1], COLS, ROWS) >= 0) { piece.push(p); continue; }
    if (piece.length >= 2) out.push(piece);
    piece = [];
  }
  return out;
}

/**
 * The chord's angle turned so the figure reads left to right.
 *
 * A ring outside the rim winds the other way round from a contour inside
 * it, because the deep side of the raster is now the far side, so the
 * chord across the gap runs right to left and the figure stood on its
 * head (owner, 2026-09-06). A chord that points left is turned by half a
 * turn; a vertical one keeps the way it came.
 */
function upright(rot: number): number {
  return Math.cos(rot) < -1e-9 ? rot + Math.PI : rot;
}

/**
 * One run cut into the pieces that stand on open water.
 *
 * A point on a blocked cell — rock, or another trench — ends the piece
 * before it, and the next open point starts a new one. A piece of one point
 * is dropped: there is no line in it.
 */
function onWater(run: Loop, blocked: Uint8Array): Loop[] {
  const out: Loop[] = [];
  let piece: Loop = [];
  for (const p of run) {
    const cell = cellAt(p[0], p[1], COLS, ROWS);
    const open = cell < 0 || blocked[cell] === 0;
    if (open) { piece.push(p); continue; }
    if (piece.length >= 2) out.push(piece);
    piece = [];
  }
  if (piece.length >= 2) out.push(piece);
  return out;
}

/** The one instance the registry holds. */
export const isobaths = new IsobathsPiece();
